import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Save, Plus, Trash2, ChevronUp, ChevronDown, ImagePlus, X, RefreshCw,
} from "lucide-react";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

// Icon list available to admin when adding a Feature card on Landing.
// Must match ICON_MAP inside Landing.jsx.
const ICON_OPTIONS = [
  "Heart", "Sparkles", "Palette", "Users", "Send", "GripVertical", "Music",
  "Calendar", "Camera", "Gift", "MessageCircle", "Shield", "Crown", "Star",
  "Check", "Award", "Bell", "Bookmark", "Cake", "Diamond", "Flower", "Zap",
];

const PLAN_IDS = ["bulan1", "bulan6", "premium"];
const PLAN_LABELS = { bulan1: "Paket 1 Bulan", bulan6: "Paket 6 Bulan", premium: "Paket Premium" };

const EMPTY = {
  hero: { badge: "", title: "", title_highlight: "", subtitle: "", cta_primary: "", cta_secondary: "" },
  features: { eyebrow: "", heading: "", description: "", items: [] },
  pricing: { eyebrow: "", heading: "", description: "",
    plans: { bulan1: { short_desc: "", features: [] }, bulan6: { short_desc: "", features: [] }, premium: { short_desc: "", features: [] } },
  },
  footer: { cta_title: "", cta_desc: "", cta_button: "", copyright_html: "", tagline: "", contact_email: "", contact_phone: "" },
  login: { background_url: "", background_upload: "", tagline_1: "", tagline_2: "", subtitle: "" },
};

function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const k of Object.keys(override)) {
    const v = override[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = deepMerge(base?.[k] || {}, v);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out;
}

function Field({ label, testid, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground" data-testid={`${testid}-label`}>{label}</Label>
      {children}
    </div>
  );
}

export default function ContentAdmin() {
  const [content, setContent] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cms");
      const merged = deepMerge(EMPTY, data?.content || {});
      // Ensure plans keys present
      for (const p of PLAN_IDS) {
        merged.pricing.plans[p] = merged.pricing.plans[p] || { short_desc: "", features: [] };
      }
      setContent(merged);
    } catch {
      toast.error("Gagal memuat konten");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/cms", { content });
      toast.success("Konten disimpan");
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const setHero = (patch) => setContent((c) => ({ ...c, hero: { ...c.hero, ...patch } }));
  const setFeatures = (patch) => setContent((c) => ({ ...c, features: { ...c.features, ...patch } }));
  const setPricing = (patch) => setContent((c) => ({ ...c, pricing: { ...c.pricing, ...patch } }));
  const setFooter = (patch) => setContent((c) => ({ ...c, footer: { ...c.footer, ...patch } }));
  const setLogin = (patch) => setContent((c) => ({ ...c, login: { ...c.login, ...patch } }));

  // ------- Feature items CRUD -------
  const addFeature = () => setFeatures({
    items: [...(content.features.items || []), { icon: "Sparkles", title: "", desc: "" }],
  });
  const updateFeature = (i, patch) => {
    const items = [...content.features.items];
    items[i] = { ...items[i], ...patch };
    setFeatures({ items });
  };
  const removeFeature = (i) => {
    const items = content.features.items.filter((_, idx) => idx !== i);
    setFeatures({ items });
  };
  const moveFeature = (i, dir) => {
    const items = [...content.features.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    [items[i], items[j]] = [items[j], items[i]];
    setFeatures({ items });
  };

  // ------- Pricing plan features CRUD -------
  const updatePlanFeature = (planId, i, value) => {
    const plans = { ...content.pricing.plans };
    const feats = [...(plans[planId].features || [])];
    feats[i] = value;
    plans[planId] = { ...plans[planId], features: feats };
    setPricing({ plans });
  };
  const addPlanFeature = (planId) => {
    const plans = { ...content.pricing.plans };
    plans[planId] = { ...plans[planId], features: [...(plans[planId].features || []), ""] };
    setPricing({ plans });
  };
  const removePlanFeature = (planId, i) => {
    const plans = { ...content.pricing.plans };
    plans[planId] = {
      ...plans[planId],
      features: (plans[planId].features || []).filter((_, idx) => idx !== i),
    };
    setPricing({ plans });
  };

  // ------- Login BG upload -------
  const onLoginBgUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await resizeImage(file, 1600);
      setLogin({ background_upload: b64 });
      toast.success("Gambar diunggah. Klik Simpan untuk menerapkan.");
    } catch {
      toast.error("Gagal mengunggah gambar");
    } finally { setUploading(false); }
  };

  if (loading) {
    return <div className="p-6 text-center text-sm text-muted-foreground" data-testid="cms-loading">Memuat…</div>;
  }

  return (
    <div className="space-y-6" data-testid="content-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitle icon={Save} title="Manajemen Konten Halaman Depan"
          subtitle="Atur teks Landing Page & Login. Kosongkan field untuk menampilkan konten default bawaan." />
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" data-testid="cms-reload">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Muat Ulang
          </Button>
          <Button onClick={save} disabled={saving} data-testid="cms-save-top">
            <Save className="mr-1.5 h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="hero" className="space-y-4">
        <TabsList className="flex flex-wrap" data-testid="cms-tabs">
          <TabsTrigger value="hero" data-testid="cms-tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="features" data-testid="cms-tab-features">Features</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="cms-tab-pricing">Pricing</TabsTrigger>
          <TabsTrigger value="footer" data-testid="cms-tab-footer">Footer</TabsTrigger>
          <TabsTrigger value="login" data-testid="cms-tab-login">Login</TabsTrigger>
        </TabsList>

        {/* HERO */}
        <TabsContent value="hero">
          <Card className="space-y-4 p-6">
            <Field label="Badge (contoh: Wedding Planner Terlengkap)" testid="hero-badge">
              <Input data-testid="cms-hero-badge" value={content.hero.badge}
                onChange={(e) => setHero({ badge: e.target.value })}
                placeholder="Kosongkan untuk pakai default" />
            </Field>
            <Field label="Judul Utama (baris 1 & 2)" testid="hero-title">
              <Textarea data-testid="cms-hero-title" value={content.hero.title}
                onChange={(e) => setHero({ title: e.target.value })}
                placeholder='Contoh: "Rencanakan Pernikahan\nImpian Anda dengan"'
                rows={2} />
            </Field>
            <Field label="Kata Highlight (huruf Great Vibes emas, contoh: Cinta)" testid="hero-title-highlight">
              <Input data-testid="cms-hero-title-highlight" value={content.hero.title_highlight}
                onChange={(e) => setHero({ title_highlight: e.target.value })}
                placeholder="Contoh: Cinta" />
            </Field>
            <Field label="Subtitle / Paragraf pengantar" testid="hero-subtitle">
              <Textarea data-testid="cms-hero-subtitle" value={content.hero.subtitle}
                onChange={(e) => setHero({ subtitle: e.target.value })}
                rows={3} placeholder="Kosongkan untuk pakai default" />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Label Tombol Utama" testid="hero-cta-primary">
                <Input data-testid="cms-hero-cta-primary" value={content.hero.cta_primary}
                  onChange={(e) => setHero({ cta_primary: e.target.value })}
                  placeholder="Contoh: Mulai Rencanakan" />
              </Field>
              <Field label="Label Tombol Sekunder" testid="hero-cta-secondary">
                <Input data-testid="cms-hero-cta-secondary" value={content.hero.cta_secondary}
                  onChange={(e) => setHero({ cta_secondary: e.target.value })}
                  placeholder="Contoh: Lihat Paket" />
              </Field>
            </div>
          </Card>
        </TabsContent>

        {/* FEATURES */}
        <TabsContent value="features">
          <Card className="space-y-4 p-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Eyebrow (uppercase kecil)" testid="feat-eyebrow">
                <Input data-testid="cms-feat-eyebrow" value={content.features.eyebrow}
                  onChange={(e) => setFeatures({ eyebrow: e.target.value })}
                  placeholder="Contoh: Fitur Wedding Planner" />
              </Field>
              <Field label="Judul Section" testid="feat-heading">
                <Input data-testid="cms-feat-heading" value={content.features.heading}
                  onChange={(e) => setFeatures({ heading: e.target.value })}
                  placeholder="Contoh: Rencanakan pernikahan Anda dengan mudah" />
              </Field>
            </div>
            <Field label="Deskripsi Section" testid="feat-desc">
              <Textarea data-testid="cms-feat-desc" value={content.features.description}
                onChange={(e) => setFeatures({ description: e.target.value })} rows={2}
                placeholder="Kosongkan untuk pakai default" />
            </Field>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm font-semibold">Daftar Fitur ({content.features.items?.length || 0} item)</p>
              <Button size="sm" onClick={addFeature} data-testid="cms-feat-add">
                <Plus className="mr-1 h-4 w-4" /> Tambah Fitur
              </Button>
            </div>

            {(content.features.items || []).length === 0 && (
              <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="cms-feat-empty">
                Belum ada fitur ditambahkan. Landing akan menampilkan 12 fitur default.
              </p>
            )}

            <div className="space-y-3">
              {(content.features.items || []).map((f, i) => (
                <div key={i} className="grid gap-3 rounded-lg border border-border p-4 md:grid-cols-[130px_1fr_auto]" data-testid={`cms-feat-row-${i}`}>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Icon</Label>
                    <Select value={f.icon || "Sparkles"} onValueChange={(v) => updateFeature(i, { icon: v })}>
                      <SelectTrigger className="mt-1" data-testid={`cms-feat-icon-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {ICON_OPTIONS.map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Input data-testid={`cms-feat-title-${i}`} placeholder="Judul fitur" value={f.title || ""}
                      onChange={(e) => updateFeature(i, { title: e.target.value })} />
                    <Textarea data-testid={`cms-feat-desc-${i}`} placeholder="Deskripsi singkat" rows={2}
                      value={f.desc || ""} onChange={(e) => updateFeature(i, { desc: e.target.value })} />
                  </div>
                  <div className="flex items-start gap-1">
                    <Button variant="outline" size="icon" onClick={() => moveFeature(i, -1)} data-testid={`cms-feat-up-${i}`}>
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => moveFeature(i, 1)} data-testid={`cms-feat-down-${i}`}>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button variant="destructive" size="icon" onClick={() => removeFeature(i)} data-testid={`cms-feat-del-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* PRICING */}
        <TabsContent value="pricing">
          <Card className="space-y-4 p-6">
            <p className="text-xs text-muted-foreground">
              Nilai harga & badge "Populer" tetap diatur dari menu <b>Harga Paket</b>. Di sini Anda dapat mengatur
              teks heading Section Pricing dan deskripsi tiap paket.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Eyebrow" testid="pricing-eyebrow">
                <Input data-testid="cms-pricing-eyebrow" value={content.pricing.eyebrow}
                  onChange={(e) => setPricing({ eyebrow: e.target.value })}
                  placeholder="Contoh: Pilihan Paket" />
              </Field>
              <Field label="Judul Section" testid="pricing-heading">
                <Input data-testid="cms-pricing-heading" value={content.pricing.heading}
                  onChange={(e) => setPricing({ heading: e.target.value })}
                  placeholder="Contoh: Pilih yang sesuai untuk Anda" />
              </Field>
            </div>
            <Field label="Deskripsi Section" testid="pricing-desc">
              <Textarea data-testid="cms-pricing-desc" value={content.pricing.description}
                onChange={(e) => setPricing({ description: e.target.value })} rows={2}
                placeholder="Kosongkan untuk pakai default" />
            </Field>

            <div className="grid gap-4 md:grid-cols-3">
              {PLAN_IDS.map((pid) => {
                const plan = content.pricing.plans[pid] || { short_desc: "", features: [] };
                return (
                  <div key={pid} className="space-y-3 rounded-lg border border-border p-4" data-testid={`cms-plan-${pid}`}>
                    <p className="font-semibold">{PLAN_LABELS[pid]}</p>
                    <Field label="Deskripsi singkat paket" testid={`plan-short-${pid}`}>
                      <Textarea data-testid={`cms-plan-short-${pid}`} value={plan.short_desc || ""}
                        onChange={(e) => {
                          const plans = { ...content.pricing.plans, [pid]: { ...plan, short_desc: e.target.value } };
                          setPricing({ plans });
                        }}
                        rows={2} placeholder="Contoh: Untuk pasangan yang butuh cepat" />
                    </Field>
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fitur ({plan.features?.length || 0})</Label>
                        <Button size="sm" variant="outline" onClick={() => addPlanFeature(pid)} data-testid={`cms-plan-add-${pid}`}>
                          <Plus className="mr-1 h-3.5 w-3.5" /> Tambah
                        </Button>
                      </div>
                      {(plan.features || []).length === 0 && (
                        <p className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
                          Kosong — akan pakai daftar fitur default.
                        </p>
                      )}
                      <div className="space-y-1.5">
                        {(plan.features || []).map((feat, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input data-testid={`cms-plan-feat-${pid}-${i}`} value={feat}
                              onChange={(e) => updatePlanFeature(pid, i, e.target.value)} placeholder="Isi fitur" />
                            <Button variant="ghost" size="icon" onClick={() => removePlanFeature(pid, i)}
                              data-testid={`cms-plan-feat-del-${pid}-${i}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer">
          <Card className="space-y-4 p-6">
            <p className="text-sm font-semibold">CTA Section (di atas footer)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Judul CTA" testid="footer-cta-title">
                <Input data-testid="cms-footer-cta-title" value={content.footer.cta_title}
                  onChange={(e) => setFooter({ cta_title: e.target.value })}
                  placeholder="Contoh: Siap mulai?" />
              </Field>
              <Field label="Label Tombol CTA" testid="footer-cta-button">
                <Input data-testid="cms-footer-cta-button" value={content.footer.cta_button}
                  onChange={(e) => setFooter({ cta_button: e.target.value })}
                  placeholder="Contoh: Masuk / Daftar Sekarang" />
              </Field>
            </div>
            <Field label="Deskripsi CTA" testid="footer-cta-desc">
              <Textarea data-testid="cms-footer-cta-desc" value={content.footer.cta_desc}
                onChange={(e) => setFooter({ cta_desc: e.target.value })} rows={2}
                placeholder="Kosongkan untuk pakai default" />
            </Field>

            <div className="my-4 h-px w-full bg-border" />
            <p className="text-sm font-semibold">Footer</p>
            <Field label="Copyright (teks polos)" testid="footer-copyright">
              <Input data-testid="cms-footer-copyright" value={content.footer.copyright_html}
                onChange={(e) => setFooter({ copyright_html: e.target.value })}
                placeholder="Contoh: © 2026 Wedifylab · Wedding Planner" />
            </Field>
            <Field label="Tagline penutup" testid="footer-tagline">
              <Input data-testid="cms-footer-tagline" value={content.footer.tagline}
                onChange={(e) => setFooter({ tagline: e.target.value })}
                placeholder="Contoh: build with ❤ untuk hari bahagia Anda" />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Email Kontak (opsional)" testid="footer-email">
                <Input data-testid="cms-footer-email" value={content.footer.contact_email}
                  onChange={(e) => setFooter({ contact_email: e.target.value })}
                  placeholder="halo@wedifylab.us" />
              </Field>
              <Field label="Nomor Telepon (opsional)" testid="footer-phone">
                <Input data-testid="cms-footer-phone" value={content.footer.contact_phone}
                  onChange={(e) => setFooter({ contact_phone: e.target.value })}
                  placeholder="+62 …" />
              </Field>
            </div>
          </Card>
        </TabsContent>

        {/* LOGIN */}
        <TabsContent value="login">
          <Card className="space-y-4 p-6">
            <p className="text-xs text-muted-foreground">
              Gambar background halaman Login (kolom kiri desktop). Prioritas: <b>Upload</b> &gt; <b>URL</b> &gt; default.
              Hanya dapat diakses oleh Admin / Sysadmin.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Upload Gambar (auto-resize 1600px)" testid="login-bg-upload">
                <div className="flex items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-muted">
                    <ImagePlus className="h-4 w-4" />
                    <span>{uploading ? "Mengunggah…" : "Pilih Berkas"}</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="cms-login-bg-file"
                      onChange={(e) => onLoginBgUpload(e.target.files?.[0])} />
                  </label>
                  {content.login.background_upload && (
                    <Button variant="ghost" size="sm" onClick={() => setLogin({ background_upload: "" })}
                      data-testid="cms-login-bg-clear-upload">
                      <X className="mr-1 h-3.5 w-3.5" /> Hapus upload
                    </Button>
                  )}
                </div>
              </Field>

              <Field label="URL Gambar (fallback)" testid="login-bg-url">
                <Input data-testid="cms-login-bg-url" value={content.login.background_url}
                  onChange={(e) => setLogin({ background_url: e.target.value })}
                  placeholder="https://..." />
              </Field>
            </div>

            {(content.login.background_upload || content.login.background_url) && (
              <div className="mt-2">
                <p className="mb-1 text-xs text-muted-foreground">Preview:</p>
                <img
                  data-testid="cms-login-bg-preview"
                  src={content.login.background_upload || content.login.background_url}
                  alt="preview"
                  className="max-h-64 w-full rounded-md border border-border object-cover"
                />
              </div>
            )}

            <div className="my-4 h-px w-full bg-border" />
            <p className="text-sm font-semibold">Teks Sambutan (kolom kiri Login desktop)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Baris 1 (font Cormorant serif)" testid="login-tagline1">
                <Input data-testid="cms-login-tagline1" value={content.login.tagline_1}
                  onChange={(e) => setLogin({ tagline_1: e.target.value })}
                  placeholder="Contoh: Selamat" />
              </Field>
              <Field label="Baris 2 (font Great Vibes emas)" testid="login-tagline2">
                <Input data-testid="cms-login-tagline2" value={content.login.tagline_2}
                  onChange={(e) => setLogin({ tagline_2: e.target.value })}
                  placeholder="Contoh: datang kembali" />
              </Field>
            </div>
            <Field label="Subtitle sambutan" testid="login-subtitle">
              <Textarea data-testid="cms-login-subtitle" value={content.login.subtitle}
                onChange={(e) => setLogin({ subtitle: e.target.value })} rows={2}
                placeholder="Kosongkan untuk pakai default" />
            </Field>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="cms-save-bottom">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
        </Button>
      </div>
    </div>
  );
}
